/**
 * Every read and write of the draft store, in one place.
 *
 * Kept apart from `db.ts` (which is schema only) and from the screens (which
 * should not know IndexedDB exists) so the sync rules in `reconcile.ts` can be
 * tested against a real store with no React in the way.
 *
 * Nothing here decides policy. Whether a draft may be flushed, what overwrote
 * what, and whether review may open are all decided in `reconcile.ts`; this module
 * only persists and returns records faithfully — including the difference between
 * a draft of `null` and no draft at all, which is rule 5's whole substance in this
 * layer.
 */
import type { CountList, CountSession } from "../api/types";
import {
  db,
  deviceId,
  itemKey,
  type DraftRecord,
  type DraftSyncState,
  type ItemRecord,
  type SessionRecord,
} from "./db";

const now = () => new Date().toISOString();

// --- Sessions ----------------------------------------------------------------

/**
 * Subscribers to the two writes that can change **where a session is allowed to
 * render**: setting and clearing `post_attempted_at`.
 *
 * D6 found why this is needed. `SessionRoute` reads the record once and then decides
 * the route from it — but a post is confirmed on S5 and lands on S7, and React Router
 * keeps the same component mounted across that move (same element type, same
 * position), so the hook never re-read the store and the marker written a moment
 * earlier was invisible. A manager whose post died at the transport was then routed
 * by status alone: back to the entry screen, over a count that might already be in
 * the ledger.
 *
 * Deliberately narrow. `recordPull` and `markFullySynced` do **not** announce: the
 * first preserves the marker by construction and neither changes any routing
 * decision, and a store that shouted on every write would re-render the entry screen
 * behind every keystroke.
 */
type SessionRecordListener = (sessionId: string) => void;

const recordListeners = new Set<SessionRecordListener>();

export function onSessionRecordChange(listener: SessionRecordListener): () => void {
  recordListeners.add(listener);
  return () => {
    recordListeners.delete(listener);
  };
}

/**
 * Tell every reader to re-read this session's record.
 *
 * Exported, and called by hand in exactly one place: the post's transport-failure
 * branch. **Persisting the marker and publishing it are deliberately separate acts.**
 * Writing it is about durability — the tab may be killed a millisecond later, and the
 * next start has to know. Publishing it *moves the manager to another screen*, and
 * doing that while the post is still in flight would tear the confirm dialog away
 * mid-request and lose the refusal it is about to show. So the write is silent, and
 * routing is told once the reply is known to be lost.
 */
export function announceSessionRecord(sessionId: string): void {
  for (const listener of [...recordListeners]) listener(sessionId);
}


export async function readSessionRecord(sessionId: string): Promise<SessionRecord | undefined> {
  return (await db()).get("sessions", sessionId);
}

/**
 * Record a session pull, preserving the two markers a pull must not disturb.
 *
 * `post_attempted_at` in particular: it is set before a post is sent and cleared
 * only by a terminal outcome, so a pull that reset it would be the app forgetting
 * that it might already have posted — the exact amnesia § 4.5 exists to prevent.
 */
export async function recordPull(
  session: CountSession,
  areaId: string,
): Promise<SessionRecord> {
  const database = await db();
  const existing = await database.get("sessions", session.session_id);
  const record: SessionRecord = {
    session_id: session.session_id,
    area_id: areaId,
    status: session.status,
    owner_upn: session.responsible_manager,
    posting_date: session.posting_date,
    pulled_at: now(),
    last_full_sync_at: existing?.last_full_sync_at ?? null,
    post_attempted_at: existing?.post_attempted_at ?? null,
  };
  await database.put("sessions", record);
  return record;
}

export async function markFullySynced(sessionId: string): Promise<void> {
  const database = await db();
  const existing = await database.get("sessions", sessionId);
  if (!existing) return;
  await database.put("sessions", { ...existing, last_full_sync_at: now() });
}

/**
 * Written **before** the post request leaves. See § 4.5.
 *
 * ⚠ **It takes the session, not just its id, and that is a D6 fix rather than a
 * convenience.** This used to return early when the device held no record for the
 * session — and a manager who deep-links to the review screen without ever opening
 * entry has no record, because `recordPull` happens at the pull. The marker would
 * then have been silently skipped for exactly one manager in exactly the case it
 * exists for: a post whose reply never arrives. So it **upserts**. A marker that
 * sometimes does not get written is worse than no marker at all, because the screen
 * that reads it would offer a Post button over a count that may already be in the
 * ledger.
 */
export async function markPostAttempted(
  session: CountSession,
  areaId: string,
): Promise<SessionRecord> {
  const database = await db();
  const existing = await database.get("sessions", session.session_id);
  const record: SessionRecord = existing
    ? { ...existing, post_attempted_at: now() }
    : {
        session_id: session.session_id,
        area_id: areaId,
        status: session.status,
        owner_upn: session.responsible_manager,
        posting_date: session.posting_date,
        pulled_at: now(),
        last_full_sync_at: null,
        post_attempted_at: now(),
      };
  await database.put("sessions", record);
  // Deliberately silent — see `announceSessionRecord`. The screen that must react to
  // this marker is the one the post's *failure* leads to, and until the request
  // settles the manager belongs on the dialog they are watching.
  return record;
}

/** Cleared only by a terminal outcome — `Posted`, or `Error`. */
export async function clearPostAttempt(sessionId: string): Promise<void> {
  const database = await db();
  const existing = await database.get("sessions", sessionId);
  if (!existing) return;
  await database.put("sessions", { ...existing, post_attempted_at: null });
  // Clearing only ever *relaxes* routing — from "ask Business Central" back to
  // whatever the session's own status affords — so publishing it immediately can
  // never strand anyone.
  announceSessionRecord(sessionId);
}

// --- The count-list snapshot -------------------------------------------------

/**
 * Snapshot the count list for offline entry. Descriptions, UOMs, sections, the
 * lot marker — and **no money**: `unit_cost` is in the payload and is not stored,
 * because a cached cost is a dollar figure BC may no longer agree with.
 */
export async function snapshotItems(sessionId: string, list: CountList): Promise<void> {
  const version = now();
  const database = await db();
  const tx = database.transaction("items", "readwrite");
  const store = tx.objectStore("items");

  // Replace rather than merge: a manager-edited count list can lose items, and a
  // stale row would offer a field for something no longer on the sheet.
  for (const stale of await store.index("by_session").getAllKeys(sessionId)) {
    await store.delete(stale);
  }
  for (const item of list.items) {
    const record: ItemRecord = {
      key: itemKey(sessionId, item.item_no),
      session_id: sessionId,
      item_no: item.item_no,
      description: item.description,
      uom: item.base_unit_of_measure,
      unit_options: item.unit_options ?? [],
      section: item.section,
      vendor: item.vendor_name,
      lot_tracked: item.lot_tracked,
      blocked: item.blocked,
      list_version: version,
    };
    await store.put(record);
  }
  await tx.done;
}

export async function readItems(sessionId: string): Promise<ItemRecord[]> {
  return (await db()).getAllFromIndex("items", "by_session", sessionId);
}

// --- Drafts ------------------------------------------------------------------

/**
 * Record what the manager typed. `null` means **not counted** and is stored as a
 * record, not as an absence.
 *
 * Always lands as `pending`: a draft becomes `synced` only when the server has
 * said so. Writing it optimistically would make the header's pending count a
 * guess, and the pending count is the one number telling a manager whether their
 * work has left the device.
 */
export async function putDraft(
  sessionId: string,
  itemNo: string,
  countedQty: string | null,
  unitOfMeasureCode?: string | null,
): Promise<DraftRecord> {
  const record: DraftRecord = {
    key: itemKey(sessionId, itemNo),
    session_id: sessionId,
    item_no: itemNo.trim().toUpperCase(),
    counted_qty: countedQty,
    typed_at: now(),
    device_id: deviceId(),
    sync_state: "pending",
    synced_at: null,
    refusal: null,
    unit_of_measure_code: unitOfMeasureCode ?? null,
  };
  await (await db()).put("drafts", record);
  return record;
}

export async function readDrafts(sessionId: string): Promise<DraftRecord[]> {
  return (await db()).getAllFromIndex("drafts", "by_session", sessionId);
}

export async function readDraft(
  sessionId: string,
  itemNo: string,
): Promise<DraftRecord | undefined> {
  return (await db()).get("drafts", itemKey(sessionId, itemNo));
}

export async function setDraftState(
  sessionId: string,
  itemNo: string,
  state: DraftSyncState,
  refusal: string | null = null,
): Promise<void> {
  const database = await db();
  const key = itemKey(sessionId, itemNo);
  const existing = await database.get("drafts", key);
  if (!existing) return;
  await database.put("drafts", {
    ...existing,
    sync_state: state,
    synced_at: state === "synced" ? now() : existing.synced_at,
    refusal,
  });
}

/**
 * Drop drafts that staging has confirmed.
 *
 * Deliberately **only** the synced ones, and only those whose typed value still
 * matches what was flushed: a manager who types again while a flush is in flight
 * must not have that keystroke swept up as "already sent".
 */
export async function forgetSynced(sessionId: string, flushed: Map<string, string | null>) {
  const database = await db();
  const tx = database.transaction("drafts", "readwrite");
  const store = tx.objectStore("drafts");
  for (const draft of await store.index("by_session").getAll(sessionId)) {
    if (!flushed.has(draft.item_no)) continue;
    if (draft.sync_state !== "synced") continue;
    if (draft.counted_qty !== flushed.get(draft.item_no)) continue;
    await store.delete(draft.key);
  }
  await tx.done;
}

/**
 * Throw away this session's drafts.
 *
 * The **explicit discard** § 4.4 requires for a session that is no longer
 * editable. Never called on the app's own initiative: silently dropping a
 * manager's typing is worse than a dialog, so this exists to be wired to a button
 * that says what it will destroy.
 */
export async function discardDrafts(sessionId: string): Promise<number> {
  const database = await db();
  const tx = database.transaction("drafts", "readwrite");
  const store = tx.objectStore("drafts");
  const keys = await store.index("by_session").getAllKeys(sessionId);
  for (const key of keys) await store.delete(key);
  await tx.done;
  return keys.length;
}

/** Everything held for a session, for the diagnostics screen's pending count. */
export async function pendingCount(sessionId?: string): Promise<number> {
  const database = await db();
  const drafts = sessionId
    ? await database.getAllFromIndex("drafts", "by_session", sessionId)
    : await database.getAll("drafts");
  return drafts.filter((draft) => draft.sync_state !== "synced").length;
}


/**
 * Drop `synced` drafts whose value staging now holds.
 *
 * Deliberately narrow. A draft is removed only when it is `synced` **and** the
 * staged quantity matches it exactly — or, for a cleared item, when staging has no
 * line at all, which is a clear that landed. Anything else stays: `pending` and
 * `refused` drafts are unsent work, and a synced draft that no longer matches
 * staging means somebody else has since written, which is the reconciliation
 * report's business rather than a thing to quietly delete.
 *
 * ⚠ **Called once when a session is opened, never on every session change.** It used
 * to run inside `recordPull`, which the entry screen calls whenever the session
 * object changes — including the change our own sync causes. The effect was that a
 * row's "saved" chip vanished the instant it appeared: the draft that was the record
 * of a successful sync got pruned by the pull triggered by that very sync. The
 * manager lost the one signal telling them their count had reached BC.
 */
export async function pruneSyncedDrafts(session: CountSession): Promise<void> {
  const database = await db();
  const staged = new Map<string, string>();
  for (const line of session.lines) {
    staged.set(line.item_no.trim().toUpperCase(), line.counted_qty);
  }

  const tx = database.transaction("drafts", "readwrite");
  const store = tx.objectStore("drafts");
  for (const draft of await store.index("by_session").getAll(session.session_id)) {
    if (draft.sync_state !== "synced") continue;
    const stagedQty = staged.get(draft.item_no) ?? null;
    if (stagedQty === draft.counted_qty) await store.delete(draft.key);
  }
  await tx.done;
}
